import React, { useContext, useCallback, useReducer, useMemo, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  PendingAction,
  PendingActionType,
  PendingActionSummary,
  PendingActionStatus,
  getExpiryForActionType,
  ACCOUNT_SETUP_EXPIRY_MS,
} from "../types/pendingActions";
import * as pendingActionsStorage from "../utils/pendingActionsStorage";
import { useUser } from "./userContext";
import PendingActionToasts from "../components/PendingActionToasts";

export type ToastEntry = { actionType: PendingActionType; summary: PendingActionSummary };
type SyncedEntry = {
  signature: string;
  actionType: PendingActionType;
  summary: PendingActionSummary;
};

type TState = {
  pendingActions: PendingAction[];
  syncedEntries: Map<string, ToastEntry>;
  expiredEntries: Map<string, ToastEntry>;
};

const initialState: TState = {
  pendingActions: [],
  syncedEntries: new Map(),
  expiredEntries: new Map(),
};

type Action =
  | { type: "ADD_PENDING_ACTION"; payload: PendingAction }
  | { type: "ADD_PENDING_ACTIONS"; payload: PendingAction[] }
  | { type: "REMOVE_PENDING_ACTION"; payload: string }
  | { type: "REMOVE_PENDING_ACTIONS_BY_GROUP"; payload: string }
  | { type: "MARK_AS_SYNCED"; payload: SyncedEntry }
  | {
      type: "MARK_AS_SYNCED_BY_ID";
      payload: { id: string; actionType: PendingActionType; summary: PendingActionSummary };
    }
  | { type: "MARK_AS_EXPIRED"; payload: SyncedEntry }
  | { type: "CLEAR_SYNCED_ENTRY"; payload: string }
  | { type: "CLEAR_EXPIRED_ENTRY"; payload: string }
  | { type: "LOAD_FROM_STORAGE"; payload: PendingAction[] }
  | { type: "CLEAR_ALL" };

function pendingActionsReducer(state: TState, action: Action): TState {
  switch (action.type) {
    case "ADD_PENDING_ACTION": {
      // Avoid duplicates by id (not signature, since batch items share signatures)
      const exists = state.pendingActions.some((a) => a.id === action.payload.id);
      if (exists) return state;
      return {
        ...state,
        pendingActions: [...state.pendingActions, action.payload],
      };
    }
    case "ADD_PENDING_ACTIONS": {
      // Add multiple actions, avoiding duplicates by id
      const existingIds = new Set(state.pendingActions.map((a) => a.id));
      const newActions = action.payload.filter((a) => !existingIds.has(a.id));
      if (newActions.length === 0) return state;
      return {
        ...state,
        pendingActions: [...state.pendingActions, ...newActions],
      };
    }
    case "REMOVE_PENDING_ACTION": {
      return {
        ...state,
        pendingActions: state.pendingActions.filter((a) => a.signature !== action.payload),
      };
    }
    case "REMOVE_PENDING_ACTIONS_BY_GROUP": {
      return {
        ...state,
        pendingActions: state.pendingActions.filter((a) => a.groupSignature !== action.payload),
      };
    }
    case "MARK_AS_SYNCED": {
      const newSynced = new Map(state.syncedEntries);
      newSynced.set(action.payload.signature, {
        actionType: action.payload.actionType,
        summary: action.payload.summary,
      });
      return {
        ...state,
        pendingActions: state.pendingActions.filter(
          (a) => a.signature !== action.payload.signature
        ),
        syncedEntries: newSynced,
      };
    }
    case "MARK_AS_SYNCED_BY_ID": {
      const actionToSync = state.pendingActions.find((a) => a.id === action.payload.id);
      if (!actionToSync) return state;
      const newSynced = new Map(state.syncedEntries);
      newSynced.set(action.payload.id, {
        actionType: action.payload.actionType,
        summary: action.payload.summary,
      });
      return {
        ...state,
        pendingActions: state.pendingActions.filter((a) => a.id !== action.payload.id),
        syncedEntries: newSynced,
      };
    }
    case "MARK_AS_EXPIRED": {
      const newExpired = new Map(state.expiredEntries);
      newExpired.set(action.payload.signature, {
        actionType: action.payload.actionType,
        summary: action.payload.summary,
      });
      return {
        ...state,
        pendingActions: state.pendingActions.filter(
          (a) => a.signature !== action.payload.signature
        ),
        expiredEntries: newExpired,
      };
    }
    case "CLEAR_SYNCED_ENTRY": {
      const newSynced = new Map(state.syncedEntries);
      newSynced.delete(action.payload);
      return { ...state, syncedEntries: newSynced };
    }
    case "CLEAR_EXPIRED_ENTRY": {
      const newExpired = new Map(state.expiredEntries);
      newExpired.delete(action.payload);
      return { ...state, expiredEntries: newExpired };
    }
    case "LOAD_FROM_STORAGE": {
      return { ...state, pendingActions: action.payload };
    }
    case "CLEAR_ALL": {
      return {
        ...state,
        pendingActions: [],
        syncedEntries: new Map(),
        expiredEntries: new Map(),
      };
    }
    default:
      return state;
  }
}

export interface AddPendingActionParams {
  signature: string;
  groupSignature?: string; // For batch operations - all items share this
  actionType: PendingActionType;
  summary: PendingActionSummary;
  destinationAddress: string;
  parentGroup: string;
  isDeleted?: boolean;
}

type TPendingActionsContext = {
  pendingActions: PendingAction[];
  syncedEntries: Map<string, ToastEntry>;
  expiredEntries: Map<string, ToastEntry>;
  pendingCount: number;
  addPendingAction: (params: AddPendingActionParams) => void;
  addPendingActions: (params: AddPendingActionParams[]) => void;
  removePendingAction: (signature: string) => void;
  removePendingActionsByGroup: (groupSignature: string) => void;
  markAsSynced: (signature: string) => void;
  markAsSyncedById: (id: string) => void;
  markAsExpired: (signature: string) => void;
  syncWithSnapshot: (snapshotSignatures: string[], voteSignatures?: string[]) => void;
  getPendingForDestination: (address: string) => PendingAction[];
  clearSyncedEntry: (signature: string) => void;
  clearExpiredEntry: (signature: string) => void;
  clearAll: () => void;
  hasPendingSafeCreation: () => boolean;
  getPendingSafeCreation: () => PendingAction | undefined;
};

const PendingActionsContext = React.createContext<TPendingActionsContext>(
  {} as TPendingActionsContext
);

export const usePendingActions = () => useContext(PendingActionsContext);

export const PendingActionsProvider = ({ children }: { children?: React.ReactNode }) => {
  const [state, dispatch] = useReducer(pendingActionsReducer, initialState);
  const { userSnapshot } = useUser();

  // Track previous values for sync detection
  const prevSafesRef = useRef<string[]>([]);
  const prevHelpersRef = useRef<string[]>([]);
  const snapshotSignaturesRef = useRef<Set<string>>(new Set());

  // Initialize from SessionStorage on mount
  useEffect(() => {
    const stored = pendingActionsStorage.getAll();
    if (stored.length > 0) {
      dispatch({ type: "LOAD_FROM_STORAGE", payload: stored });
    }
  }, []);

  // Sync state to SessionStorage on every change
  useEffect(() => {
    pendingActionsStorage.saveAll(state.pendingActions);
  }, [state.pendingActions]);

  // Auto-expire check every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();

      state.pendingActions.forEach((action) => {
        // Check if this action's signature appears in the snapshot
        // For votes, their signature (from sendRequest) appears as voteSIGNATURE in phase data
        // For other actions, their signature appears as SIGNATURE in the actions list
        // Both are stored in snapshotSignaturesRef by syncWithSnapshot
        if (snapshotSignaturesRef.current.has(action.signature)) {
          dispatch({
            type: "MARK_AS_SYNCED_BY_ID",
            payload: { id: action.id, actionType: action.actionType, summary: action.summary },
          });
          return;
        }

        // Only expire if NOT in snapshot and past expiry time
        const expiryTime = getExpiryForActionType(action.actionType);
        const isExpired = now - action.timestamp > expiryTime;

        if (isExpired) {
          // For account_setup, we don't remove but show "taking longer" (handled in UI)
          if (action.actionType !== "account_setup") {
            dispatch({
              type: "MARK_AS_EXPIRED",
              payload: {
                signature: action.signature,
                actionType: action.actionType,
                summary: action.summary,
              },
            });
          }
        }
      });
    }, 10000);

    return () => clearInterval(interval);
  }, [state.pendingActions]);

  // Auto-sync account_setup when safes appear
  useEffect(() => {
    if (!userSnapshot?.safes) return;

    const currentSafes = userSnapshot.safes || [];
    const prevSafes = prevSafesRef.current;

    // Check if new safes appeared
    const newSafes = currentSafes.filter((s) => !prevSafes.includes(s));

    if (newSafes.length > 0) {
      // Find pending account_setup actions with includesSafe
      const accountSetupPending = state.pendingActions.filter(
        (a) => a.actionType === "account_setup" && a.summary.includesSafe && a.status === "pending"
      );

      // Mark them as synced
      accountSetupPending.forEach((pending) => {
        dispatch({
          type: "MARK_AS_SYNCED",
          payload: {
            signature: pending.signature,
            actionType: pending.actionType,
            summary: pending.summary,
          },
        });
      });
    }

    prevSafesRef.current = currentSafes;
  }, [userSnapshot?.safes, state.pendingActions]);

  // Auto-sync helper actions when helpers change
  useEffect(() => {
    if (!userSnapshot?.helperAddresses) return;

    const currentHelpers = userSnapshot.helperAddresses || [];
    const prevHelpers = prevHelpersRef.current;

    // Check if helpers changed (added, removed, or updated)
    const helpersChanged =
      currentHelpers.length !== prevHelpers.length ||
      currentHelpers.some((h, i) => h !== prevHelpers[i]);

    if (helpersChanged && prevHelpers.length > 0) {
      // Find pending helper actions
      const helperPending = state.pendingActions.filter(
        (a) => a.actionType === "helper" && a.status === "pending"
      );

      // Mark them as synced
      helperPending.forEach((pending) => {
        dispatch({
          type: "MARK_AS_SYNCED",
          payload: {
            signature: pending.signature,
            actionType: pending.actionType,
            summary: pending.summary,
          },
        });
      });
    }

    prevHelpersRef.current = currentHelpers;
  }, [userSnapshot?.helperAddresses, state.pendingActions]);

  const generateAddPendingAction = useCallback((params: AddPendingActionParams) => {
    const timestamp = Date.now();
    return {
      id: uuidv4(),
      signature: params.signature,
      groupSignature: params.groupSignature,
      actionType: params.actionType,
      summary: params.summary,
      timestamp: timestamp,
      destinationAddress: params.destinationAddress,
      parentGroup: params.parentGroup,
      status: "pending" as const,
      isDeleted: params.isDeleted || false,
    };
  }, []);

  const addPendingAction = useCallback(
    (params: AddPendingActionParams) => {
      const newAction: PendingAction = generateAddPendingAction(params);
      dispatch({ type: "ADD_PENDING_ACTION", payload: newAction });
    },
    [generateAddPendingAction]
  );

  const addPendingActions = useCallback(
    (params: AddPendingActionParams[]) => {
      const newActions: PendingAction[] = params.map((p) => generateAddPendingAction(p));
      dispatch({ type: "ADD_PENDING_ACTIONS", payload: newActions });
    },
    [generateAddPendingAction]
  );

  const removePendingAction = useCallback((signature: string) => {
    dispatch({ type: "REMOVE_PENDING_ACTION", payload: signature });
  }, []);

  const removePendingActionsByGroup = useCallback((groupSignature: string) => {
    dispatch({ type: "REMOVE_PENDING_ACTIONS_BY_GROUP", payload: groupSignature });
  }, []);

  const markAsSynced = useCallback(
    (signature: string) => {
      const action = state.pendingActions.find((a) => a.signature === signature);
      const actionType = action?.actionType || "transaction";
      const summary = action?.summary || {};
      dispatch({ type: "MARK_AS_SYNCED", payload: { signature, actionType, summary } });
    },
    [state.pendingActions]
  );

  const markAsSyncedById = useCallback(
    (id: string) => {
      const action = state.pendingActions.find((a) => a.id === id);
      const actionType = action?.actionType || "transaction";
      const summary = action?.summary || {};
      dispatch({ type: "MARK_AS_SYNCED_BY_ID", payload: { id, actionType, summary } });
    },
    [state.pendingActions]
  );

  const markAsExpired = useCallback(
    (signature: string) => {
      const action = state.pendingActions.find((a) => a.signature === signature);
      const actionType = action?.actionType || "transaction";
      const summary = action?.summary || {};
      dispatch({ type: "MARK_AS_EXPIRED", payload: { signature, actionType, summary } });
    },
    [state.pendingActions]
  );

  const syncWithSnapshot = useCallback(
    (snapshotSignatures: string[], voteSignatures?: string[]) => {
      // Store latest snapshot signatures for the auto-expire check
      const allSignatures = new Set([...snapshotSignatures, ...(voteSignatures || [])]);
      snapshotSignaturesRef.current = allSignatures;

      // Find pending actions whose signatures are now in the snapshot
      state.pendingActions.forEach((action) => {
        if (action.status !== "pending") return;

        // For vote actions, check if their command signature appears as a voteSIGNATURE
        if (action.actionType === "vote") {
          if (voteSignatures?.includes(action.signature)) {
            dispatch({
              type: "MARK_AS_SYNCED_BY_ID",
              payload: { id: action.id, actionType: action.actionType, summary: action.summary },
            });
          }
          return;
        }

        // For non-vote actions, check the regular signature
        if (snapshotSignatures.includes(action.signature)) {
          dispatch({
            type: "MARK_AS_SYNCED_BY_ID",
            payload: { id: action.id, actionType: action.actionType, summary: action.summary },
          });
        }
      });
    },
    [state.pendingActions]
  );

  const getPendingForDestination = useCallback(
    (address: string): PendingAction[] => {
      return state.pendingActions.filter(
        (a) => a.destinationAddress === address && a.status === "pending"
      );
    },
    [state.pendingActions]
  );

  const clearSyncedEntry = useCallback((signature: string) => {
    dispatch({ type: "CLEAR_SYNCED_ENTRY", payload: signature });
  }, []);

  const clearExpiredEntry = useCallback((signature: string) => {
    dispatch({ type: "CLEAR_EXPIRED_ENTRY", payload: signature });
  }, []);

  const clearAll = useCallback(() => {
    dispatch({ type: "CLEAR_ALL" });
    pendingActionsStorage.clear();
  }, []);

  const hasPendingSafeCreation = useCallback((): boolean => {
    return state.pendingActions.some(
      (a) => a.actionType === "account_setup" && a.summary.includesSafe && a.status === "pending"
    );
  }, [state.pendingActions]);

  const getPendingSafeCreation = useCallback((): PendingAction | undefined => {
    return state.pendingActions.find(
      (a) => a.actionType === "account_setup" && a.summary.includesSafe && a.status === "pending"
    );
  }, [state.pendingActions]);

  const pendingCount = useMemo(
    () => state.pendingActions.filter((a) => a.status === "pending").length,
    [state.pendingActions]
  );

  const contextValue = useMemo(
    () => ({
      pendingActions: state.pendingActions,
      syncedEntries: state.syncedEntries,
      expiredEntries: state.expiredEntries,
      pendingCount,
      addPendingAction,
      addPendingActions,
      removePendingAction,
      removePendingActionsByGroup,
      markAsSynced,
      markAsSyncedById,
      markAsExpired,
      syncWithSnapshot,
      getPendingForDestination,
      clearSyncedEntry,
      clearExpiredEntry,
      clearAll,
      hasPendingSafeCreation,
      getPendingSafeCreation,
    }),
    [
      state.pendingActions,
      state.syncedEntries,
      state.expiredEntries,
      pendingCount,
      addPendingAction,
      addPendingActions,
      removePendingAction,
      removePendingActionsByGroup,
      markAsSynced,
      markAsSyncedById,
      markAsExpired,
      syncWithSnapshot,
      getPendingForDestination,
      clearSyncedEntry,
      clearExpiredEntry,
      clearAll,
      hasPendingSafeCreation,
      getPendingSafeCreation,
    ]
  );

  return (
    <PendingActionsContext.Provider value={contextValue}>
      {children}
      <PendingActionToasts />
    </PendingActionsContext.Provider>
  );
};
